import { AlertTriangle, Package, Sparkles } from 'lucide-react'
import { formatMoney, cn } from '@/lib/utils'
import { AiData } from '../ui/ai-data'
import { AiProgress, AiStep, type AiStepStatus } from '../ui/ai-motion'
import { aiEnter, useArrivals } from '../ui/ai-motion-state'
import { SurfaceCard } from '../ui/page'
import { type LiveImportState, liveProgress } from './live-import-state'
import { defaultCandidate, modelDifference } from './review'
import type { ExtractedRow, ImportTool, MatchCandidate, ReviewedRow } from './types'

// The live read of a document (demo `features/imports/live-import.tsx`): rows arrive one by one,
// each with its catalog steps, then its recommended product.

const matchLabels: Record<MatchCandidate['match_type'], string> = {
  exact: 'Exact match',
  close_match: 'Close match',
  preferred_alternative: 'Preferred',
  budget_alternative: 'Budget',
  faster_alternative: 'Faster lead time',
  ai_suggested: 'AI suggested',
}

const leadDays = (candidate: MatchCandidate) =>
  candidate.lead_maximum_days == null
    ? null
    : candidate.lead_minimum_days === candidate.lead_maximum_days
      ? `${candidate.lead_maximum_days} days`
      : `${candidate.lead_minimum_days}-${candidate.lead_maximum_days} days`

const matchTones: Partial<Record<MatchCandidate['match_type'], string>> = {
  exact: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300',
  close_match: 'bg-amber-50 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
}

/** A close match's model, with what differs from the printed model marked. */
function CatalogModel({ candidate, printedModel }: { candidate: MatchCandidate; printedModel?: string | null }) {
  if (candidate.match_type !== 'close_match' || !printedModel) return candidate.model
  const [before, changed, after] = modelDifference(printedModel, candidate.model)
  return (
    <>
      {before}
      {changed ? <mark className="rounded-sm bg-amber-100 px-px text-amber-900 dark:bg-amber-900/60 dark:text-amber-100">{changed}</mark> : null}
      {after}
    </>
  )
}

/** The recommended product at a glance: picture, identity, category, price and lead time. */
export function MatchCard({
  candidate,
  printedModel,
  labelled = true,
  className,
}: {
  candidate: MatchCandidate
  /** The document's model, to mark what a close match changes. */
  printedModel?: string | null
  /** Shows the match kind; off where the list's heading already names it. */
  labelled?: boolean
  className?: string
}) {
  const facts = [
    candidate.list_amount != null ? `List ${formatMoney(candidate.list_amount)}` : null,
    candidate.net_amount != null ? `Net ${formatMoney(candidate.net_amount)}` : null,
    leadDays(candidate),
  ].filter(Boolean)
  return (
    <div className={cn('flex min-w-0 items-center gap-3', className)}>
      <span className="grid size-12 shrink-0 place-items-center overflow-hidden rounded-md border bg-white">
        {candidate.image_url ? (
          <img alt="" className="size-full object-contain" loading="lazy" src={candidate.image_url} />
        ) : (
          <Package aria-hidden="true" className="size-5 text-muted-foreground" />
        )}
      </span>
      <div className="min-w-0">
        {/* A long manufacturer name gives way first; the model always shows. */}
        <p className="flex min-w-0 flex-wrap items-center gap-x-1.5 text-sm font-semibold">
          <span className="min-w-0 truncate">{candidate.manufacturer}</span>
          <span className="min-w-0 [overflow-wrap:anywhere]">
            <CatalogModel candidate={candidate} printedModel={printedModel} />
          </span>
          {labelled ? (
            <span
              className={cn(
                'shrink-0 rounded-full px-1.5 py-px text-[11px] font-medium',
                matchTones[candidate.match_type] ?? 'bg-violet-50 text-violet-700 dark:bg-violet-950 dark:text-violet-300',
              )}
            >
              {matchLabels[candidate.match_type]}
            </span>
          ) : null}
        </p>
        <p className="truncate text-xs text-muted-foreground">{candidate.category || candidate.title}</p>
        {facts.length ? <p className="mt-0.5 text-xs tabular-nums text-muted-foreground">{facts.join(' · ')}</p> : null}
      </div>
    </div>
  )
}

// Catalog identity and price already show on the product card; these results do not.
const cardless = new Set<ImportTool>(['options'])

const toolLabels: Record<ImportTool, string> = {
  catalog: 'Catalog',
  pricing: 'Price',
  options: 'Options',
}

function stageLabel(state: LiveImportState) {
  if (state.stage === 'opening') return `Opening ${state.fileName}`
  if (state.stage === 'reading') return `Reading ${state.fileName}`
  if (state.stage === 'matching') return 'Matching the catalog'
  return 'Saving review'
}

function Shimmer() {
  return (
    <span
      aria-hidden="true"
      className="absolute inset-y-0 left-0 w-1/3 animate-[qb-ai-shimmer_1.4s_ease-in-out_infinite] bg-gradient-to-r from-transparent via-white/70 to-transparent dark:via-white/10"
    />
  )
}

function CardSkeleton() {
  return (
    <div aria-hidden="true" className="relative flex items-center gap-3 overflow-hidden rounded-md border px-2.5 py-2">
      <span className="size-12 shrink-0 rounded-md bg-muted" />
      <span className="flex-1 space-y-1.5">
        <span className="block h-2.5 w-3/4 rounded bg-muted" />
        <span className="block h-2 w-1/2 rounded bg-muted" />
      </span>
      <Shimmer />
    </div>
  )
}

function SkeletonRow() {
  return (
    <div
      aria-hidden="true"
      className="relative grid gap-3 overflow-hidden border-b px-4 py-3 last:border-b-0 sm:grid-cols-[minmax(0,1fr)_minmax(240px,0.8fr)]"
    >
      <div className="space-y-2 py-1">
        <span className="block h-3 w-2/5 rounded bg-muted" />
        <span className="block h-2.5 w-3/5 rounded bg-muted" />
      </div>
      <CardSkeleton />
    </div>
  )
}

function LiveRow({
  row,
  reviewed,
  steps,
  fresh,
}: {
  row: ExtractedRow
  reviewed?: ReviewedRow
  steps?: LiveImportState['steps'][string]
  fresh: boolean
}) {
  const candidate = reviewed ? defaultCandidate(reviewed.candidates) : undefined
  const closeCount = reviewed?.candidates.filter((item) => item.match_type === 'close_match').length ?? 0
  return (
    <article
      className={cn(
        'grid min-w-0 gap-3 border-b px-4 py-3 last:border-b-0 sm:grid-cols-[minmax(0,1fr)_minmax(240px,0.8fr)]',
        row.parent_row_id && 'ml-6 border-l',
        fresh && aiEnter,
      )}
    >
      <div className="min-w-0">
        <div className="flex min-w-0 items-center gap-2">
          {row.symbol ? (
            <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 font-mono text-[11px] font-medium text-muted-foreground">
              {row.symbol}
            </span>
          ) : null}
          <p className="truncate text-sm font-semibold">
            {row.manufacturer ?? 'No manufacturer'} {row.model ?? ''}
          </p>
          <span className="shrink-0 text-xs tabular-nums text-muted-foreground">Qty {Number(row.quantity)}</span>
        </div>
        <p className="mt-0.5 truncate text-xs text-muted-foreground">{row.description}</p>
        {steps ? (
          <div className="mt-2 flex flex-wrap gap-1">
            {(Object.keys(toolLabels) as ImportTool[])
              .filter((tool) => steps[tool])
              .map((tool) => (
                <AiStep key={tool} label={toolLabels[tool]} status={steps[tool]!.status}>
                  {steps[tool]!.status === 'done' && cardless.has(tool) ? steps[tool]!.summary : null}
                </AiStep>
              ))}
          </div>
        ) : null}
      </div>
      <div className="min-w-0">
        {candidate ? (
          <AiData className={cn('px-2.5 py-2', aiEnter)} label="AI-assisted catalog recommendation">
            <MatchCard candidate={candidate} printedModel={row.model} />
          </AiData>
        ) : reviewed ? (
          <p
            className={cn(
              'flex items-center gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-2 text-xs font-medium text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300',
              aiEnter,
            )}
          >
            <AlertTriangle aria-hidden="true" className="size-3.5" />
            {closeCount
                ? `${closeCount} close match${closeCount === 1 ? '' : 'es'}`
                : 'No catalog match'}
          </p>
        ) : (
          <CardSkeleton />
        )}
      </div>
    </article>
  )
}

export function LiveImport({ state }: { state: LiveImportState }) {
  const fresh = useArrivals(state.rows.map((row) => row.row_id))
  const freshWarnings = useArrivals(state.warnings)
  const matched = Object.values(state.matched)
  const exact = matched.filter((row) => row.candidates[0]?.match_type === 'exact').length
  const reading = state.stage === 'opening' || state.stage === 'reading'
  const stages: Array<{ key: string; label: string; status: AiStepStatus; summary?: string }> = [
    {
      key: 'open',
      label: 'Open',
      status: state.stage === 'opening' ? 'running' : 'done',
      summary: state.stage === 'opening' ? undefined : 'Document ready',
    },
    ...(state.stage !== 'opening'
      ? [
          {
            key: 'read',
            label: 'Read',
            status: reading ? ('running' as const) : ('done' as const),
            summary: reading ? undefined : `${state.rows.length} items`,
          },
        ]
      : []),
    ...(state.rows.length
      ? [
          {
            key: 'match',
            label: 'Match',
            status: reading || state.stage === 'matching' ? ('running' as const) : ('done' as const),
            summary: `${exact} exact`,
          },
        ]
      : []),
    ...(state.stage === 'saving' ? [{ key: 'save', label: 'Save', status: 'running' as const }] : []),
  ]
  return (
    <SurfaceCard>
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3">
        <div className="flex min-w-0 items-center gap-2">
          <Sparkles aria-hidden="true" className="size-4 shrink-0 animate-pulse text-violet-600 motion-reduce:animate-none" />
          <h3 className="truncate text-sm font-semibold" role="status">
            {stageLabel(state)}
          </h3>
        </div>
        <span className="text-xs tabular-nums text-muted-foreground">
          {state.rows.length ? `${matched.length} of ${state.rows.length} matched` : 'Finding equipment rows'}
        </span>
      </div>
      <AiProgress value={liveProgress(state)} />
      <div className="flex flex-wrap gap-1 border-b px-4 py-2">
        {stages.map((stage) => (
          <AiStep key={stage.key} label={stage.label} status={stage.status}>
            {stage.status === 'done' || stage.key === 'match' ? stage.summary : null}
          </AiStep>
        ))}
      </div>
      <div aria-label="Items found so far" aria-live="off">
        {!state.rows.length ? [0, 1, 2].map((index) => <SkeletonRow key={index} />) : null}
        {state.rows.map((row) => (
          <LiveRow
            fresh={fresh.has(row.row_id)}
            key={row.row_id}
            reviewed={state.matched[row.row_id]}
            row={row}
            steps={state.steps[row.row_id]}
          />
        ))}
      </div>
      {/* Warnings arrive under the rows, so the rows being read never move down. */}
      {state.warnings.map((warning) => (
        <p
          className={cn(
            'flex gap-2 border-t border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200',
            freshWarnings.has(warning) && aiEnter,
          )}
          key={warning}
        >
          <AlertTriangle aria-hidden="true" className="mt-0.5 size-3.5 shrink-0 text-amber-600" />
          {warning}
        </p>
      ))}
    </SurfaceCard>
  )
}
